import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Flag, Heart, Reply, Send, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reader comments for any story — auto-publish with guardrails (server side),
 * report button per comment. `accent`/`onAccent` let it live on both brands:
 * Lowdown passes cherry/white, Rhythm passes off-white/ink.
 */
export default function CommentSection({ articleType, slug, accent = "#FF0055", onAccent = "#FFFFFF" }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: "", email: "", body: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [liked, setLiked] = useState({});
  const [replyTo, setReplyTo] = useState(null); // {id, name} — set when replying
  const formRef = useRef(null);
  const textareaRef = useRef(null);

  // Clicking Reply brings the writer to the form: the replying chip + textarea
  // live at the top of the section, so scroll it into view and focus the box —
  // otherwise a Reply click deep in the thread looks like nothing happened.
  const startReply = (id, name) => {
    setReplyTo({ id, name });
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => textareaRef.current?.focus(), 400);
    });
  };

  const { data: comments = [] } = useQuery({
    queryKey: ["comments", articleType, slug],
    queryFn: async () => {
      try {
        const { data } = await axios.get(`${API}/comments/${articleType}/${slug}`);
        return data;
      } catch {
        return [];
      }
    },
  });

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/comments/${articleType}/${slug}`, {
        ...form,
        parent_id: replyTo?.id || "",
      });
      if (!data.ok) throw new Error("post failed");
      setForm({ name: "", email: "", body: "", website: "" });
      setReplyTo(null);
      qc.invalidateQueries({ queryKey: ["comments", articleType, slug] });
      toast.success(replyTo ? "Reply posted — they'll know." : "Posted — welcome to the group chat.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't post that — try again.");
    } finally {
      setBusy(false);
    }
  };

  const report = async (id) => {
    try {
      await axios.post(`${API}/comments/${id}/report`);
      toast.success("Reported — thank you. We'll take a look.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't report — try again.");
    }
  };

  // Hearts feed the Glowgetter Wall. One per browser per comment.
  const like = async (id) => {
    if (localStorage.getItem(`cgd-liked-${id}`)) return;
    localStorage.setItem(`cgd-liked-${id}`, "1");
    setLiked((l) => ({ ...l, [id]: true }));
    try {
      await axios.post(`${API}/comments/${id}/like`);
      qc.invalidateQueries({ queryKey: ["comments", articleType, slug] });
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error("Easy — one love at a time.");
        return;
      }
      localStorage.removeItem(`cgd-liked-${id}`);
      setLiked((l) => ({ ...l, [id]: false }));
      toast.error("Couldn't drop that heart — try again.");
    }
  };

  // Hearts feed the Glowgetter Wall. One per browser per comment.
  // Dep is a stable id-string (not the array ref) — `= []` defaults create a
  // fresh reference each render and would loop this effect forever.
  const commentKey = comments.map((c) => c.id).join(",");
  useEffect(() => {
    setLiked((l) => {
      const seeded = { ...l };
      commentKey.split(",").filter(Boolean).forEach((id) => {
        if (localStorage.getItem(`cgd-liked-${id}`)) seeded[id] = true;
      });
      return seeded;
    });
  }, [commentKey]);

  return (
    <section data-testid="comments-section" className="mt-16 border-t border-white/10 pt-10">
      <h2 className="font-display text-xl font-extrabold uppercase tracking-tight sm:text-2xl">
        The group chat{" "}
        <span className="text-white/40" data-testid="comments-count">
          ({comments.length})
        </span>
      </h2>

      <form ref={formRef} onSubmit={submit} className="mt-6 space-y-3 scroll-mt-28" data-testid="comment-form">
        {replyTo && (
          <div
            data-testid="replying-chip"
            style={{ borderColor: `${accent}55`, backgroundColor: `${accent}14` }}
            className="flex items-center justify-between gap-3 rounded-2xl border px-4 py-2.5"
          >
            <span className="text-xs font-bold text-white/80">
              Replying to <span style={{ color: accent }}>{replyTo.name}</span>
            </span>
            <button
              type="button"
              data-testid="reply-cancel-btn"
              onClick={() => setReplyTo(null)}
              aria-label="Cancel reply"
              className="rounded-full p-1 text-white/50 transition-colors hover:bg-white/10 hover:text-white"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <input
            data-testid="comment-name-input"
            value={form.name}
            onChange={set("name")}
            placeholder="Your name"
            maxLength={40}
            className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-white/35 focus:border-white/40 focus:outline-none"
          />
          <input
            data-testid="comment-email-input"
            value={form.email}
            onChange={set("email")}
            placeholder="Email (optional — never shown; get reply alerts)"
            type="email"
            maxLength={120}
            className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-white/35 focus:border-white/40 focus:outline-none"
          />
        </div>
        <textarea
          ref={textareaRef}
          data-testid="comment-body-input"
          value={form.body}
          onChange={set("body")}
          placeholder="Say the thing…"
          rows={3}
          maxLength={1000}
          className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm leading-relaxed text-white placeholder:text-white/35 focus:border-white/40 focus:outline-none"
        />
        {/* honeypot — hidden from humans, catnip for bots */}
        <input
          value={form.website}
          onChange={set("website")}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="pointer-events-none absolute -left-[9999px] h-0 w-0 opacity-0"
        />
        <button
          data-testid="comment-submit-btn"
          disabled={busy}
          style={{ backgroundColor: accent, color: onAccent }}
          className="inline-flex items-center gap-2.5 rounded-full px-6 py-3 text-xs font-bold uppercase tracking-[0.14em] transition-transform duration-300 hover:scale-[1.04] disabled:opacity-50"
        >
          <Send className="h-3.5 w-3.5" /> {busy ? "Posting…" : "Post it"}
        </button>
      </form>

      <div className="mt-8 space-y-4">
        {comments.length === 0 && (
          <p data-testid="comments-empty" className="text-sm text-white/45">
            No comments yet — start the group chat.
          </p>
        )}
        {comments
          .filter((c) => !c.parent_id)
          .map((c) => {
            // Replies to this comment, oldest first — conversation order.
            const replies = comments
              .filter((r) => r.parent_id === c.id)
              .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
            return (
              <div key={c.id} data-testid={`comment-item-${c.id}`} className="space-y-3">
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                  <div className="flex items-start gap-3">
                    <span
                      aria-hidden="true"
                      style={{ backgroundColor: `${accent}26`, color: accent }}
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold"
                    >
                      {(c.name || "?").trim().charAt(0).toUpperCase()}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="text-sm font-bold text-white">{c.name}</span>
                        <span className="text-[11px] text-white/40">
                          {new Date(c.created_at).toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                          })}
                        </span>
                      </p>
                      <p className="mt-1.5 whitespace-pre-wrap break-words text-sm leading-relaxed text-white/75">
                        {c.body}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        data-testid={`comment-reply-${c.id}`}
                        onClick={() => startReply(c.id, c.name)}
                        aria-label="Reply to comment"
                        className="inline-flex items-center gap-1 rounded-full px-2 py-1.5 text-[11px] font-bold text-white/30 transition-colors hover:bg-white/5 hover:text-white/80"
                      >
                        <Reply className="h-3.5 w-3.5" /> Reply
                      </button>
                      <button
                        data-testid={`comment-like-${c.id}`}
                        onClick={() => like(c.id)}
                        disabled={liked[c.id]}
                        aria-label="Like comment"
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-1.5 text-[11px] font-bold transition-colors ${
                          liked[c.id]
                            ? "text-cherry"
                            : "text-white/30 hover:bg-white/5 hover:text-white/80"
                        }`}
                      >
                        <Heart className={`h-3.5 w-3.5 ${liked[c.id] ? "fill-cherry" : ""}`} />
                        {c.likes > 0 ? c.likes : ""}
                      </button>
                      <button
                        data-testid={`comment-report-${c.id}`}
                        onClick={() => report(c.id)}
                        aria-label="Report comment"
                        className="shrink-0 rounded-full p-1.5 text-white/30 transition-colors hover:bg-white/5 hover:text-white/80"
                      >
                        <Flag className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
                {replies.length > 0 && (
                  <div data-testid={`comment-replies-${c.id}`} className="ml-6 space-y-3 sm:ml-10">
                    {replies.map((r) => (
                      <div
                        key={r.id}
                        data-testid={`comment-item-${r.id}`}
                        style={{ borderLeftColor: `${accent}40` }}
                        className="rounded-2xl border border-white/10 border-l-2 bg-white/[0.02] p-4"
                      >
                        <div className="flex items-start gap-3">
                          <span
                            aria-hidden="true"
                            style={{ backgroundColor: `${accent}26`, color: accent }}
                            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold"
                          >
                            {(r.name || "?").trim().charAt(0).toUpperCase()}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="flex flex-wrap items-center gap-x-3 gap-y-1">
                              <span className="text-xs font-bold text-white">{r.name}</span>
                              {r.reply_to_name && (
                                <span className="text-[11px]" style={{ color: accent }}>
                                  ↳ {r.reply_to_name}
                                </span>
                              )}
                              <span className="text-[11px] text-white/40">
                                {new Date(r.created_at).toLocaleDateString(undefined, {
                                  month: "short",
                                  day: "numeric",
                                })}
                              </span>
                            </p>
                            <p className="mt-1 whitespace-pre-wrap break-words text-[13px] leading-relaxed text-white/70">
                              {r.body}
                            </p>
                          </div>
                          <button
                            data-testid={`comment-reply-${r.id}`}
                            onClick={() => startReply(c.id, r.name)}
                            aria-label="Reply to comment"
                            className="shrink-0 rounded-full p-1.5 text-white/30 transition-colors hover:bg-white/5 hover:text-white/80"
                          >
                            <Reply className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </section>
  );
}
